/**
 * THE FILLING FLOOR — the company's own cylinders (0145).
 *
 * The other half of the production pillar, and until now the navigation said "later" where
 * this screen is. What it replaces is worse than an empty menu item: without it a cylinder
 * that came back empty could never be filled again, so sending it out needed a manager's
 * written exception for dispatching an empty — for a drum that was about to be full.
 *
 * ACTION BEFORE INFORMATION. The screen opens on the four things a shift can DO, in the order
 * the work happens: fill it, check what went in, empty the one that failed. The batch register
 * and a cylinder's own history come after, because they answer questions rather than prompting
 * work. Nothing here is a status to be decoded — each list is named for its next step.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session, type Views } from '../api.js';
import { useAsk, useRun, go, useRouteQuery } from '../hooks.js';
import { Banner, Button, Card, Kpi, Loading, Pill, Register, ScanBox, Select, Serial, Text, TextArea, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { FleetTabs } from '../fleet.js';
import { count, date, today } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('filling')!.messages;

type Floor = Views['filling.floor']['result'];
type FloorCylinder = Floor['toFill'][number];
type Batch = Views['filling.batches']['result']['rows'][number];

/** Which inline form is open, and on which cylinder. Only one at a time: a filling floor is
 *  one person at one manifold, and two open forms is two half-finished records. */
type Open = { what: 'fill' | 'check' | 'prepare' | 'inspect'; cylinder: FloorCylinder } | null;

/* ================================================================== 11 Oct 2026: CTS1's Filling, on CTS2's facts
 *
 * The owner, with eight CTS1 screenshots: "this needs to be reworked completely… map it
 * well". So: a throughput strip; a scan-or-pick queue with a gas filter that fills many
 * cylinders in one go against one batch; and the five tabs CTS1 has — Waiting, Filled
 * (verify, reverse), History, By gas, By customer — plus Batches, which CTS1 lacks and the
 * laboratory needs. The yard's own stages (to check, failed, not offered) stay under Waiting.
 */
type WaitingRow = Views['filling.waiting']['result']['rows'][number];
type FilledRow = Views['filling.filled']['result']['rows'][number];
type HistoryRow = Views['filling.history']['result']['rows'][number];
type Tab = 'waiting' | 'filled' | 'history' | 'gas' | 'customer' | 'batches';
interface Queued { cylinderId: number; internalId: string; serialNo: string | null; gasService: string; kind: 'ours' | 'customer'; ownerName: string | null; jobId: number | null; qty: string; unit: 'm3' | 'kg'; pressure: string }

function unitFor(gas: string | null): 'm3' | 'kg' { return /acet|co2|carbon|n2o|nitrous|lpg|propane|ammonia/i.test(gas ?? '') ? 'kg' : 'm3'; }
function defaultQty(gas: string | null): string { return unitFor(gas) === 'kg' ? '' : '7'; }

export function Filling({ session }: { session: Session }) {
  const [open, setOpen] = useState<Open>(null);
  const [history, setHistory] = useState<FloorCylinder | null>(null);
  const tabRaw = useRouteQuery('tab');
  const tab: Tab = (['waiting', 'filled', 'history', 'gas', 'customer', 'batches'] as const).includes(tabRaw as Tab) ? (tabRaw as Tab) : 'waiting';
  const [gasFilter, setGasFilter] = useState('');
  const [queue, setQueue] = useState<Queued[]>([]);
  const [scan, setScan] = useState('');
  const [scanNote, setScanNote] = useState<string | null>(null);
  const [batchRef, setBatchRef] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [fillOn, setFillOn] = useState(today());
  const [progress, setProgress] = useState<string | null>(null);
  const [tick, setTick] = useState(0);   // bumps when anything was filled/checked/emptied, so every list re-reads
  const runner = useRun(MESSAGES);

  const stats = useAsk(() => api.view('filling.stats', {}), [], MESSAGES);
  const byGas = useAsk(() => api.view('filling.byGas', {}), [], MESSAGES);
  const floor = useAsk(() => api.view('filling.floor', { limit: 200 }), [], MESSAGES);
  const pick = useAsk(() => api.view('filling.waiting', { page: 1, pageSize: 60, sort: 'since', dir: 'asc', ...(gasFilter ? { gas: gasFilter } : {}) }), [gasFilter], MESSAGES);

  const mayFill = can(session, 'fill.record');
  const mayJobFill = can(session, 'job.fill');
  const mayCheck = can(session, 'fill.check');
  const mayPrepare = can(session, 'fill.prepare');
  const mayInspect = can(session, 'cylinder.inspect');

  function reloadAll() { stats.reload(); byGas.reload(); floor.reload(); pick.reload(); setTick((t) => t + 1); }
  function done() { setOpen(null); reloadAll(); }

  function enqueue(r: { cylinderId: number; internalId: string; serialNo: string | null; gasService: string | null; kind: 'ours' | 'customer'; ownerName: string | null; jobId: number | null }) {
    if (queue.some((q) => q.cylinderId === r.cylinderId)) { setScanNote(`${r.internalId} is already on the queue`); return; }
    setQueue((old) => [...old, { cylinderId: r.cylinderId, internalId: r.internalId, serialNo: r.serialNo, gasService: r.gasService ?? '', kind: r.kind, ownerName: r.ownerName, jobId: r.jobId,
      qty: defaultQty(r.gasService), unit: unitFor(r.gasService), pressure: '' }]);
    setScanNote(null);
  }
  async function addScanned() {
    const token = scan.trim(); if (!token) return;
    const hit = (pick.data?.rows ?? []).find((r) => r.internalId.toLowerCase() === token.toLowerCase() || (r.serialNo ?? '').toLowerCase() === token.toLowerCase());
    if (hit) { enqueue(hit); setScan(''); return; }
    try {
      const res = await api.view('cylinders.resolve', { token });
      if (res.total === 0) { setScanNote(`No cylinder answers to "${token}".`); return; }
      if (res.total > 1) { setScanNote(`"${token}" is more than one cylinder — type its CTS number.`); return; }
      const c = res.candidates[0]!;
      const w = await api.view('filling.waiting', { q: c.internalId, page: 1, pageSize: 5 });
      const row = w.rows.find((r) => r.cylinderId === c.cylinderId);
      if (!row) { setScanNote(`${c.internalId} is not waiting to be filled — it is ${String((c as { state?: string }).state ?? 'elsewhere').replace(/_/g, ' ')}.`); return; }
      enqueue(row); setScan('');
    } catch (e) { setScanNote(e instanceof Error ? e.message : 'could not look that up'); }
  }
  async function fillQueue() {
    if (!batchRef.trim() || queue.length === 0) return;
    let doneN = 0;
    for (const q of queue) {
      setProgress(`Filling ${q.internalId} (${doneN + 1} of ${queue.length})…`);
      const ok = await runner.run((retryKey) => {
        const body = { cylinderId: q.cylinderId, gasService: q.gasService, quantity: Number(q.qty), quantityUnit: q.unit,
          ...(q.pressure.trim() ? { pressureBar: Number(q.pressure) } : {}),
          batch: { reference: batchRef.trim(), ...(sourceRef.trim() ? { sourceRef: sourceRef.trim() } : {}) }, filledOn: fillOn };
        return q.kind === 'customer' && q.jobId
          ? api.command('job.fill', { jobId: q.jobId, ...body }, retryKey ? { idempotencyKey: retryKey } : {})
          : api.command('fill.record', body, retryKey ? { idempotencyKey: retryKey } : {});
      });
      if (!ok) { setProgress(null); setQueue((old) => old.slice(doneN)); reloadAll(); return; }
      doneN += 1;
    }
    setProgress(`${doneN} filled against ${batchRef.trim()}.`); setQueue([]); reloadAll();
  }
  const queueReady = queue.length > 0 && batchRef.trim() !== '' && queue.every((q) => q.gasService.trim() !== '' && Number(q.qty) > 0);

  const SUB = 'Everything waiting at the manifold — ours and customers\' — in the order the work happens.';
  const st = stats.data; const f = floor.data;
  const gases = byGas.data ?? [];
  const tabs: { id: Tab; label: string; n?: number | undefined }[] = [
    { id: 'waiting', label: 'Waiting', n: f?.counts.toFill },
    { id: 'filled', label: 'Filled', n: f ? f.counts.toCheck + f.counts.saleable : undefined },
    { id: 'history', label: 'History' }, { id: 'gas', label: 'By gas' }, { id: 'customer', label: 'By customer' }, { id: 'batches', label: 'Batches' },
  ];

  return (
    <Page title="Filling" sub={SUB}>
      <FleetTabs active="filling" session={session} />
      {open ? <WorkForm open={open} onDone={done} onCancel={() => setOpen(null)} /> : null}
      {history ? <CycleHistory cylinder={history} onClose={() => setHistory(null)} /> : null}

      {/* THE THROUGHPUT STRIP (CTS1): filled today, last 7 days, gas in 7 days, turnaround, top fillers */}
      <div className="kpis" data-filling-stats>
        <Kpi label="Filled today" value={count(st?.filledToday ?? 0)} note="cylinders" tone="good" />
        <Kpi label="Last 7 days" value={count(st?.filled7 ?? 0)} note="cylinders" />
        <Kpi label="Gas · 7 days" value={st ? `${st.gas7.m3} Nm³` : '—'} note={st ? `${st.gas7.kg} kg` : ''} tone="primary" />
        <Kpi label="Avg turnaround" value={st?.avgTurnaroundDays == null ? '—' : `${st.avgTurnaroundDays}d`} note="came back → filled" />
        <Kpi label="Top fillers · 7d" value={st?.topFillers[0]?.name ?? '—'} note={(st?.topFillers ?? []).map((t) => `${t.name} ${t.fills}`).join(' · ')} />
      </div>

      {/* THE QUEUE: scan or pick, gas filter, cards on the right, one batch, one button */}
      {mayFill || mayJobFill ? (
        <div className="grid two" style={{ alignItems: 'start' }}>
          <Card title="Scan or pick what to fill" sub="Scan or type any serial; it joins the queue on the right. Ours and customers' alike — a customer's is filled against their job.">
            <Select name="gas-filter" label="Gas · filters the list" value={gasFilter} onChange={setGasFilter}
                    options={[{ value: '', label: 'All gases' }, ...gases.map((g) => ({ value: g.gasService, label: `${g.gasService} (${count(g.empty)} empty)` }))]} />
            <ScanBox label="Cylinder serial" value={scan} onChange={setScan} onAdd={() => { void addScanned(); }} {...(scanNote ? { help: scanNote } : {})} />
            <Select name="pick-waiting" label={`Or pick from the waiting list (${count(pick.data?.page.total ?? 0)})`} value="" placeholder="Pick a cylinder…"
                    onChange={(v) => { const r = (pick.data?.rows ?? []).find((x) => String(x.cylinderId) === v); if (r) enqueue(r); }}
                    options={(pick.data?.rows ?? []).filter((r) => !queue.some((q) => q.cylinderId === r.cylinderId)).map((r) => ({
                      value: String(r.cylinderId), label: `${r.internalId} · ${r.gasService ?? '—'} · ${r.kind === 'ours' ? 'ours' : r.ownerName ?? 'customer'}${r.held ? ' · HELD' : r.notInspected ? ' · not inspected' : ''}` }))} />
          </Card>
          <Card title={queue.length ? `Queue · ${count(queue.length)}` : 'Queue is empty'}
                sub={queue.length ? 'Check each quantity, name the batch, and fill them all in one go.' : 'Scan or type a serial on the left. Each one you add appears here as a card.'}>
            {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
            {progress ? <Banner kind={queue.length ? 'info' : 'good'} title={progress} /> : null}
            {queue.map((q) => (
              <div key={q.cylinderId} className="queue-item" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 0.7fr 0.8fr auto', gap: 8, alignItems: 'end', padding: '8px 0', borderBottom: '1px solid var(--line, #e3e6ea)' }}>
                <div><Serial>{q.internalId}</Serial><div className="muted">{q.serialNo ?? ''}{q.kind === 'customer' ? ` · ${q.ownerName ?? 'customer'}` : ' · ours'}</div></div>
                <Text name={`q-gas-${q.cylinderId}`} label="Gas" value={q.gasService} onChange={(v) => setQueue((old) => old.map((x) => x.cylinderId === q.cylinderId ? { ...x, gasService: v } : x))} />
                <Text name={`q-qty-${q.cylinderId}`} label="Qty" type="number" value={q.qty} onChange={(v) => setQueue((old) => old.map((x) => x.cylinderId === q.cylinderId ? { ...x, qty: v } : x))} />
                <Select name={`q-unit-${q.cylinderId}`} label="Unit" value={q.unit} onChange={(v) => setQueue((old) => old.map((x) => x.cylinderId === q.cylinderId ? { ...x, unit: v as 'm3' | 'kg' } : x))} options={[{ value: 'm3', label: 'Nm³' }, { value: 'kg', label: 'Kg' }]} />
                <Button small onClick={() => setQueue((old) => old.filter((x) => x.cylinderId !== q.cylinderId))}>Remove</Button>
              </div>
            ))}
            {queue.length ? (
              <div className="grid three" style={{ marginTop: 12 }}>
                <Text name="queue-batch" label="Batch" value={batchRef} onChange={setBatchRef} required placeholder="e.g. B-2026-10-11-A" />
                <Text name="queue-source" label="Gas came from" value={sourceRef} onChange={setSourceRef} placeholder="tank, trailer, generator" />
                <Text name="queue-on" label="Filled on" type="date" value={fillOn} onChange={setFillOn} />
              </div>
            ) : null}
            <Button kind="primary" wide disabled={!queueReady || runner.busy} onClick={() => { void fillQueue(); }}>
              {queue.length === 0 ? 'Scan or type a cylinder to begin' : runner.busy ? 'Filling…' : `Fill ${count(queue.length)} cylinder${queue.length === 1 ? '' : 's'}`}
            </Button>
          </Card>
        </div>
      ) : null}

      <div className="seg" role="tablist" aria-label="Filling" style={{ margin: '14px 0 10px' }}>
        {tabs.map((t) => (
          <button key={t.id} type="button" className={`seg-btn${tab === t.id ? ' on' : ''}`} onClick={() => go(t.id === 'waiting' ? '#/filling' : `#/filling?tab=${t.id}`)}>
            {t.label}{t.n !== undefined ? ` ${count(t.n)}` : ''}
          </button>
        ))}
      </div>

      {tab === 'waiting' ? (
        floor.loading && !f ? <Loading what="the filling floor" />
        : floor.error ? <Banner kind="error" title={floor.error.title} {...(floor.error.detail ? { detail: floor.error.detail } : {})} />
        : <>
          <WaitingList tick={tick} gas={gasFilter} onQueue={(r) => enqueue(r)} onFill={mayFill ? (r) => { const c = f!.toFill.find((x) => x.cylinderId === r.cylinderId); if (c) setOpen({ what: 'fill', cylinder: c }); else enqueue(r); } : null}
                       onHistory={(r) => setHistory({ cylinderId: r.cylinderId, internalId: r.internalId } as FloorCylinder)} />
          <Bucket what="tocheck" title="Filled, and the gas has not been checked" rows={f!.toCheck} tone="testing" total={f!.counts.toCheck}
                  action={mayCheck ? { label: 'Record the check', go: (c) => setOpen({ what: 'check', cylinder: c }) } : null} onHistory={setHistory} />
          <Bucket what="failed" title="Failed its check — empty it before anything else goes in" rows={f!.toEmpty} tone="danger" total={f!.counts.toEmpty}
                  action={mayPrepare ? { label: 'Empty it', go: (c) => setOpen({ what: 'prepare', cylinder: c }) } : null} onHistory={setHistory} />
          <Bucket what="saleable" title="Checked — ours may go out, a customer's goes home" rows={f!.saleable} tone="good" total={f!.counts.saleable} action={null} onHistory={setHistory} />
          <Bucket what="notoffered" title="Not offered as work — and why" rows={f!.notOffered} tone="danger" total={f!.counts.notOffered} why
                  action={mayInspect ? { label: 'Inspect it', go: (c) => setOpen({ what: 'inspect', cylinder: c }), only: (c) => c.blockedBy.some((b) => /condition|inspect/i.test(b)) } : null} onHistory={setHistory} />
          {f!.filledBeforeCts > 0 ? <p className="muted"><b>{count(f!.filledBeforeCts)}</b> cylinder(s) read as filled with no filling of ours behind them — they came in that way at registration or cutover. They may still go out.</p> : null}
        </>
      ) : null}
      {tab === 'filled' ? <FilledList tick={tick} session={session} onChanged={reloadAll} /> : null}
      {tab === 'history' ? <HistoryList tick={tick} /> : null}
      {tab === 'gas' ? (
        <Card title="Company cylinders · by gas" sub={`${count(gases.reduce((a, g) => a + g.empty, 0))} to fill · ${count(gases.reduce((a, g) => a + g.filled, 0))} ready`}>
          <Register rows={gases} empty="No company cylinder is standing in the plant." columns={[
            { key: 'gas', head: 'Gas', cell: (g) => <strong>{g.gasService}</strong> },
            { key: 'empty', head: 'Empty', right: true, cell: (g) => <a href={`#/cylinders?state=empty_in_plant&gasService=${encodeURIComponent(g.gasService)}`}>{count(g.empty)}</a> },
            { key: 'filled', head: 'Filled', right: true, cell: (g) => <a href={`#/cylinders?state=filled_in_plant&gasService=${encodeURIComponent(g.gasService)}`}>{count(g.filled)}</a> },
          ]} />
        </Card>
      ) : null}
      {tab === 'customer' ? <ByCustomer /> : null}
      {tab === 'batches' ? <Batches /> : null}
    </Page>
  );
}

function WaitingList({ tick, gas, onQueue, onFill, onHistory }: { tick: number; gas: string; onQueue: (r: WaitingRow) => void; onFill: ((r: WaitingRow) => void) | null; onHistory: (r: WaitingRow) => void }) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const asked = useAsk(() => api.view('filling.waiting', { page, pageSize: 25, sort: 'since', dir: 'asc', ...(q.trim() ? { q: q.trim() } : {}), ...(gas ? { gas } : {}) }), [page, q, gas, tick], MESSAGES);
  const columns: readonly Column<WaitingRow>[] = [
    { key: 'cyl', head: 'Cylinder', cell: (r) => <><a href={`#/cylinders?open=${r.cylinderId}`}><Serial>{r.internalId}</Serial></a>{r.serialNo ? <div className="muted">stamped {r.serialNo}</div> : null}</> },
    { key: 'whose', head: 'Whose', cell: (r) => r.kind === 'ours' ? <Pill tone="empty">ours</Pill> : <><Pill tone="testing">{r.ownerName ?? 'customer'}</Pill></> },
    { key: 'gas', head: 'Gas', cell: (r) => r.gasService ?? <span className="muted">—</span> },
    { key: 'since', head: 'Waiting since', cell: (r) => date(r.since) },
    { key: 'state', head: '', cell: (r) => r.held ? <Pill tone="danger">held</Pill> : r.notInspected ? <Pill tone="testing">not inspected</Pill> : null },
    { key: 'do', head: '', cell: (r) => <>
        {!r.held ? <Button small onClick={() => onQueue(r)}>+ Queue</Button> : null}{' '}
        {onFill && !r.held && r.kind === 'ours' ? <Button small kind="primary" onClick={() => onFill(r)}>Fill it</Button> : null}{' '}
        <Button small onClick={() => onHistory(r)}>History</Button></> },
  ];
  return (
    <Card title="Waiting to be filled" sub={`${count(asked.data?.page.total ?? 0)} cylinders across ours and customers'`}
          actions={<Text name="waiting-q" label="" value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search serial or party…" />}>
      {asked.loading && !asked.data ? <Loading what="the waiting list" />
        : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
        : <><div data-bucket="tofill"><Register columns={columns} rows={asked.data!.rows} empty="Nothing is waiting to be filled." /></div><Pager page={asked.data!.page} onPage={setPage} /></>}
    </Card>
  );
}

function FilledList({ tick, session, onChanged }: { tick: number; session: Session; onChanged: () => void }) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [reversing, setReversing] = useState<FilledRow | null>(null);
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);
  const asked = useAsk(() => api.view('filling.filled', { page, pageSize: 25, sort: 'filled', dir: 'desc', ...(q.trim() ? { q: q.trim() } : {}) }), [page, q, tick], MESSAGES);
  const mayReverse = can(session, 'fill.check') && can(session, 'fill.prepare');
  async function reverse() {
    if (!reversing) return;
    // A wrong fill is REVERSED in writing: the check records that the gas is not accepted, with the
    // reason, and the cylinder is emptied — two facts, both kept, never a deleted row.
    const ok = await runner.run(async (retryKey) => {
      await api.command('fill.check', { cylinderId: reversing.cylinderId, outcome: 'failed', reason: `reversed: ${reason.trim()}`, checkedOn: today() }, retryKey ? { idempotencyKey: retryKey } : {});
      return api.command('fill.prepare', { cylinderId: reversing.cylinderId, method: 'vented', reason: `fill reversed: ${reason.trim()}`, preparedOn: today() });
    });
    if (ok) { setReversing(null); setReason(''); asked.reload(); onChanged(); }
  }
  const columns: readonly Column<FilledRow>[] = [
    { key: 'cyl', head: 'Serial', cell: (r) => <><a href={`#/cylinders?open=${r.cylinderId}`}><Serial>{r.internalId}</Serial></a>{r.serialNo ? <div className="muted">{r.serialNo}</div> : null}</> },
    { key: 'for', head: 'For', cell: (r) => r.kind === 'ours' ? <Pill tone="good">ours</Pill> : <Pill tone="testing">{r.ownerName ?? 'customer'}</Pill> },
    { key: 'gas', head: 'Gas', cell: (r) => r.gasService ?? '—' },
    { key: 'qty', head: 'Gas filled', right: true, cell: (r) => <strong>{r.quantity ?? '—'} {r.quantityUnit === 'm3' ? 'Nm³' : r.quantityUnit === 'kg' ? 'Kg' : ''}</strong> },
    { key: 'on', head: 'Filled at', cell: (r) => <>{date(r.filledOn)}<div className="muted">{r.filledBy ?? ''}{r.batchReference ? ` · ${r.batchReference}` : ''}</div></> },
    { key: 'qc', head: 'Check', cell: (r) => r.qcOutcome === null ? <Pill tone="testing">not checked</Pill> : <Pill tone={r.qcOutcome === 'passed' ? 'good' : 'danger'}>{r.qcOutcome}</Pill> },
    { key: 'do', head: '', cell: (r) => mayReverse && r.kind === 'ours' ? <Button small kind="danger" onClick={() => { setReversing(r); setReason(''); }}>Reverse</Button> : null },
  ];
  return (
    <Card title="Filled — verify quantities" sub={`${count(asked.data?.page.total ?? 0)} cylinders standing full`}
          actions={<Text name="filled-q" label="" value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search serial, customer or gas…" />}>
      {reversing ? (
        <Card title={`Reverse the fill of ${reversing.internalId}`} sub="The check records the gas as not accepted, with your reason, and the cylinder is emptied. Nothing is deleted.">
          {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
          <TextArea name="reverse-reason" label="Why" value={reason} onChange={setReason} />
          <div className="toolbar"><Button kind="danger" disabled={!reason.trim() || runner.busy} onClick={() => { void reverse(); }}>Reverse it</Button><Button onClick={() => setReversing(null)}>Keep it</Button></div>
        </Card>
      ) : null}
      {asked.loading && !asked.data ? <Loading what="the filled cylinders" />
        : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
        : <><Register columns={columns} rows={asked.data!.rows} empty="No cylinder is standing full in the plant." /><Pager page={asked.data!.page} onPage={setPage} /></>}
    </Card>
  );
}

function HistoryList({ tick }: { tick: number }) {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const asked = useAsk(() => api.view('filling.history', { page, pageSize: 25, sort: 'filled', dir: 'desc', ...(q.trim() ? { q: q.trim() } : {}) }), [page, q, tick], MESSAGES);
  const columns: readonly Column<HistoryRow>[] = [
    { key: 'cyl', head: 'Cylinder', cell: (r) => <><a href={`#/cylinders?open=${r.cylinderId}`}><Serial>{r.internalId}</Serial></a>{r.serialNo ? <div className="muted">{r.serialNo}</div> : null}</> },
    { key: 'owner', head: 'Owner', cell: (r) => r.kind === 'ours' ? 'Ours' : (r.ownerName ?? 'customer') },
    { key: 'gas', head: 'Gas', cell: (r) => r.gasService ?? '—' },
    { key: 'qty', head: 'Qty', right: true, cell: (r) => <strong>{r.quantity ?? '—'} {r.quantityUnit === 'm3' ? 'Nm³' : r.quantityUnit === 'kg' ? 'Kg' : ''}</strong> },
    { key: 'by', head: 'Filled by', cell: (r) => r.filledBy ?? '—' },
    { key: 'on', head: 'Date', cell: (r) => <>{date(r.filledOn)}<div className="muted">{r.batchReference ?? ''}</div></> },
    { key: 'status', head: 'Status', cell: (r) => <>
        {r.qcOutcome === 'failed' ? <Pill tone="danger">failed check</Pill>
          : r.nowState === 'at_customer' || r.nowState === 'in_transit' ? <Pill tone="primary">dispatched</Pill>
          : r.nowState === 'empty_in_plant' ? <Pill tone="empty">returned</Pill>
          : r.nowState === 'filled_in_plant' ? <Pill tone="good">in stock</Pill>
          : <Pill tone="muted">{(r.nowState ?? '').replace(/_/g, ' ')}</Pill>}</> },
  ];
  return (
    <Card title="Fill history" sub={`Every fill, newest first · ${count(asked.data?.page.total ?? 0)} records`}
          actions={<Text name="history-q" label="" value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search serial, gas, customer, operator…" />}>
      {asked.loading && !asked.data ? <Loading what="the fill history" />
        : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
        : <><Register columns={columns} rows={asked.data!.rows} empty="Nothing has been filled here yet." /><Pager page={asked.data!.page} onPage={setPage} /></>}
    </Card>
  );
}

function ByCustomer() {
  const [q, setQ] = useState('');
  const asked = useAsk(() => api.view('filling.byCustomer', { ...(q.trim() ? { q: q.trim() } : {}) }), [q], MESSAGES);
  type Row = Views['filling.byCustomer']['result'][number];
  return (
    <Card title="Customer cylinders · by customer" sub="Their own cylinders in for filling: waiting, and filled but not yet gone home."
          actions={<Text name="bycust-q" label="" value={q} onChange={setQ} placeholder="Search customer name…" />}>
      {asked.loading && !asked.data ? <Loading what="the customers" />
        : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
        : <Register<Row> rows={asked.data!} empty="No customer has cylinders in for filling." onOpen={(r) => go(`#/customers/${r.customerId}?tab=jobs`)} columns={[
            { key: 'c', head: 'Customer', cell: (r) => <strong>{r.customerName}</strong> },
            { key: 'w', head: 'Wait', right: true, cell: (r) => <span style={{ color: 'var(--money, #b45309)', fontWeight: 600 }}>{count(r.waiting)}</span> },
            { key: 'f', head: 'Fill', right: true, cell: (r) => <span style={{ color: 'var(--good, #15803d)', fontWeight: 600 }}>{count(r.filled)}</span> },
          ]} />}
    </Card>
  );
}

function Batches() {
  const [page, setPage] = useState(1);
  const batches = useAsk(() => api.view('filling.batches', { page, pageSize: 25 }), [page], MESSAGES);
  return (
    <Card title="Batches" sub="Where the gas came from, and what came out of it.">
      {batches.loading && !batches.data ? <Loading what="the batches" />
        : batches.error ? <Banner kind="error" title={batches.error.title} {...(batches.error.detail ? { detail: batches.error.detail } : {})} />
        : <><Register columns={BATCH_COLUMNS} rows={batches.data!.rows} empty="No batch has been opened yet. The first fill opens one." /><Pager page={batches.data!.page} onPage={setPage} /></>}
    </Card>
  );
}

function Bucket({ what, title, rows, tone, action, onHistory, total, why }: {
  what: string; title: string; rows: readonly FloorCylinder[];
  tone: 'good' | 'danger' | 'testing' | 'empty';
  action: { label: string; go: (c: FloorCylinder) => void; only?: (c: FloorCylinder) => boolean } | null;
  onHistory: (c: FloorCylinder) => void;
  /** the bucket's TRUE size, so a capped list says what it is not showing */
  total?: number;
  /** show WHY instead of the fill, for the bucket that is a list of problems */
  why?: boolean;
}) {
  if (rows.length === 0) return null;
  const columns: readonly Column<FloorCylinder>[] = [
    { key: 'cyl', head: 'Cylinder', cell: (r) => (
        <><a href={`#/cylinders?open=${r.cylinderId}`}><Serial>{r.internalId}</Serial></a>
          {r.serialNo ? <div className="muted">stamped {r.serialNo}</div> : null}</>) },
    // R20261005-12 — WHOSE WORK IT IS. A work type, not a separate screen: the stages are the
    // same and the manifold queue is one queue. A customer's cylinder names him, because
    // "whose is this" is the first thing a yard hand asks about a drum that is not ours.
    { key: 'whose', head: 'Work', cell: (r) => r.workType === 'customer_job'
        ? <><Pill tone="testing">job work</Pill>
            <div className="muted">{r.customerName ?? 'a customer'}</div></>
        : <Pill tone="empty">our stock</Pill> },
    { key: 'stage', head: 'Stage', cell: (r) => (
        <><strong>{r.stage}</strong>{r.nextAction ? <div className="muted">{r.nextAction}</div> : null}</>) },
    { key: 'gas', head: 'Gas', cell: (r) => r.gasService
        ? <>{r.gasService}{r.quantity ? <div className="muted">{r.quantity} {r.quantityUnit}</div> : null}</>
        : <span className="muted">—</span> },
    { key: 'cycle', head: 'Fill', cell: (r) => r.cycleNo === null
        ? <span className="muted">never filled here</span>
        : <>#{r.cycleNo}<div className="muted">{date(r.filledOn)}</div></> },
    // THE SENTENCE, NOT THE WORD. "failed" tells the next shift nothing; what was wrong with
    // the gas is the only part of a failure that is any use to them.
    ...(why
      ? [{ key: 'blocked', head: 'What blocks it',
           cell: (r: FloorCylinder) => <>{r.blockedBy.join('; ')}</> } as Column<FloorCylinder>,
         // THE OWNER'S FOUR QUESTIONS, answered on the row rather than inferred from the
         // card's title: where it is, what stage, what blocks it, what happens next.
         { key: 'next', head: 'Next action',
           cell: (r: FloorCylinder) => <><strong>{r.stage}</strong><div className="muted">{r.nextAction}</div></> } as Column<FloorCylinder>]
      : [{ key: 'why', head: 'What was wrong',
           cell: (r: FloorCylinder) => r.failureReason ?? <span className="muted">—</span> } as Column<FloorCylinder>]),
    { key: 'area', head: 'Standing in', cell: (r) => r.areaCode
        ? <>{r.areaCode}<div className="muted">{(r.areaKind ?? '').replace(/_/g, ' ')}</div></>
        : <span className="muted">area not recorded</span> },
    { key: 'do', head: '', cell: (r) => (
        <>{/* A CUSTOMER'S CYLINDER IS NOT FILLED FROM HERE. `fill.record` refuses it by name
              (`fill.not_our_cylinder`) because job work is charged against his job; offering
              the button and letting the command refuse would be the screen contradicting the
              rules. The row says where that work is done instead. */}
          {r.workType === 'customer_job'
            ? <a href={`#/customers?job=${r.jobId}`}
                 onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(`#/customers?job=${r.jobId}`); }}>
                on his job →
              </a>
            : action && (action.only === undefined || action.only(r))
            ? <Button small kind="primary" onClick={() => action.go(r)}>{action.label}</Button> : null}
          {' '}<Button small onClick={() => onHistory(r)}>History</Button></>) },
  ];
  return (
    <Card title={title}
          actions={<Pill tone={tone}>{count(total ?? rows.length)}</Pill>}
          {...(total !== undefined && total > rows.length
            ? { sub: `Showing ${count(rows.length)} of ${count(total)} — the rest are on this list too, and this screen is capped.` }
            : {})}>
      <div data-bucket={what}><Register columns={columns} rows={rows} empty="" /></div>
    </Card>
  );
}

/* ------------------------------------------------------------------ the three forms */

function WorkForm({ open, onDone, onCancel }: { open: NonNullable<Open>; onDone: () => void; onCancel: () => void }) {
  const c = open.cylinder;
  const runner = useRun(MESSAGES);

  // fill
  const [gas, setGas] = useState(c.gasService ?? '');
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState('m3');
  const [pressure, setPressure] = useState('');
  const [batchRef, setBatchRef] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [analysisRef, setAnalysisRef] = useState('');
  // check
  const [outcome, setOutcome] = useState('passed');
  const [purity, setPurity] = useState('');
  // prepare
  const [method, setMethod] = useState('purged');
  // inspect (0160) — the SAME findings a return records, so the yard has one vocabulary
  const [finding, setFinding] = useState('ok');
  // shared
  const [reason, setReason] = useState('');
  const [on, setOn] = useState(today());

  async function submit() {
    const ok = await runner.run((retryKey) => {
      if (open.what === 'fill') {
        return api.command('fill.record', {
          cylinderId: c.cylinderId, gasService: gas.trim(),
          quantity: Number(qty), quantityUnit: unit as 'kg' | 'm3',
          ...(pressure.trim() ? { pressureBar: Number(pressure) } : {}),
          batch: { reference: batchRef.trim(),
                   ...(sourceRef.trim() ? { sourceRef: sourceRef.trim() } : {}),
                   ...(analysisRef.trim() ? { analysisRef: analysisRef.trim() } : {}) },
          filledOn: on,
        }, retryKey ? { idempotencyKey: retryKey } : {});
      }
      if (open.what === 'inspect') {
        return api.command('cylinder.inspect', {
          cylinderId: c.cylinderId, finding: finding as 'ok',
          ...(reason.trim() ? { note: reason.trim() } : {}),
          inspectedOn: on,
        }, retryKey ? { idempotencyKey: retryKey } : {});
      }
      if (open.what === 'check') {
        return api.command('fill.check', {
          cylinderId: c.cylinderId, outcome: outcome as 'passed' | 'failed',
          ...(purity.trim() ? { purityPct: Number(purity) } : {}),
          ...(pressure.trim() ? { pressureBar: Number(pressure) } : {}),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          checkedOn: on,
        });
      }
      return api.command('fill.prepare', {
        cylinderId: c.cylinderId,
        method: method as 'purged' | 'evacuated' | 'vented' | 'valve_replaced' | 'other',
        reason: reason.trim(), preparedOn: on,
      });
    });
    if (ok) onDone();
  }

  const title = open.what === 'fill' ? `Fill ${c.internalId}`
              : open.what === 'check' ? `Check the gas in ${c.internalId}`
              : open.what === 'inspect' ? `What condition is ${c.internalId} in?`
              : `Empty ${c.internalId}`;
  // A failed check and a preparation both NEED a reason; a passed check does not.
  const needsReason = open.what === 'prepare' || (open.what === 'check' && outcome === 'failed');
  const ready = open.what === 'fill'
    ? gas.trim() !== '' && qty.trim() !== '' && Number(qty) > 0 && batchRef.trim() !== ''
    : open.what === 'inspect' ? finding !== ''
    : !needsReason || reason.trim() !== '';

  return (
    <Card title={title}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}

      {open.what === 'fill' ? (
        <div className="grid two">
          <div>
            <Text name="fill-gas" label="Gas" value={gas} onChange={setGas} required autoFocus />
            <Text name="fill-qty" label="Quantity" type="number" value={qty} onChange={setQty} required />
            <Select name="fill-unit" label="Unit" value={unit} onChange={setUnit}
                    options={[{ value: 'm3', label: 'Nm³' }, { value: 'kg', label: 'Kg' }]} />
            <Text name="fill-pressure" label="Pressure (bar)" type="number" value={pressure} onChange={setPressure} />
          </div>
          <div>
            {/* A BATCH IS A ROW, NOT A LABEL. The same reference is the same batch; a new one
                has to say where the gas came from, because a batch that cannot say that
                traces nothing. The command refuses it and this says so first. */}
            <Text name="fill-batch" label="Batch" value={batchRef} onChange={setBatchRef} required
                  help="The same reference is the same batch. A new batch must say where the gas came from." />
            <Text name="fill-source" label="Gas came from" value={sourceRef} onChange={setSourceRef}
                  help="The tank, the bank, or the tanker's docket. Needed only when the batch is new." />
            <Text name="fill-analysis" label="Analysis reference" value={analysisRef} onChange={setAnalysisRef} />
            <Text name="fill-on" label="Filled on" type="date" value={on} onChange={setOn} required />
          </div>
        </div>
      ) : null}

      {open.what === 'check' ? (
        <div className="grid two">
          <div>
            <Select name="check-outcome" label="Verdict" value={outcome} onChange={setOutcome}
                    options={[{ value: 'passed', label: 'Passed — the gas may be sold' }, { value: 'failed', label: 'Failed' }]} />
            <Text name="check-purity" label="Purity (%)" type="number" value={purity} onChange={setPurity} />
            <Text name="check-pressure" label="Pressure (bar)" type="number" value={pressure} onChange={setPressure} />
          </div>
          <div>
            <TextArea name="check-reason" label={outcome === 'failed' ? 'What was wrong with the gas' : 'Note (optional)'}
                      value={reason} onChange={setReason} />
            <Text name="check-on" label="Checked on" type="date" value={on} onChange={setOn} required />
            {outcome === 'failed' ? (
              <p className="muted">A failure does not condemn the cylinder. It stays here until the drum is
                 emptied and recorded, and it cannot go to a customer in the meantime.</p>
            ) : (
              <p className="muted">A pass is a verdict on the GAS. It does not release the vessel — a
                 quarantined cylinder stays quarantined and its holds stand.</p>
            )}
          </div>
        </div>
      ) : null}

      {/* 0160 — THE STAGE BETWEEN RETURNED AND FILLABLE. The same findings a return records,
          because it is the same question: what condition is this cylinder in. A finding that
          stops a vessel quarantines it here exactly as it would on arrival. */}
      {open.what === 'inspect' ? (
        <div className="grid two">
          <div>
            <Select name="inspect-finding" label="What you found" value={finding} onChange={setFinding}
                    options={[{ value: 'ok', label: 'Sound' },
                              { value: 'leaking', label: 'Leaking' },
                              { value: 'valve_damaged', label: 'Valve damaged' },
                              { value: 'body_damage', label: 'Body damaged' },
                              { value: 'fire_exposure', label: 'Exposed to fire' },
                              { value: 'unknown_contents', label: 'Contents unknown' },
                              { value: 'other_safety_defect', label: 'Another safety defect' },
                              { value: 'cosmetic_damage', label: 'Cosmetic damage only' }]} />
            <Text name="inspect-on" label="Looked at on" type="date" value={on} onChange={setOn} required />
          </div>
          <div>
            <TextArea name="inspect-note" label="Note" value={reason} onChange={setReason} />
            <p className="muted">Nobody recorded what condition this drum came back in, so it is not offered
               for filling until somebody has looked. Anything that stops a cylinder puts it straight
               into isolation, exactly as it would on arrival.</p>
          </div>
        </div>
      ) : null}

      {open.what === 'prepare' ? (
        <div className="grid two">
          <div>
            <Select name="prep-method" label="How" value={method} onChange={setMethod}
                    options={[{ value: 'purged', label: 'Purged' }, { value: 'evacuated', label: 'Evacuated' },
                              { value: 'vented', label: 'Vented' }, { value: 'valve_replaced', label: 'Valve replaced' },
                              { value: 'other', label: 'Something else' }]} />
            <Text name="prep-on" label="Done on" type="date" value={on} onChange={setOn} required />
          </div>
          <div>
            <TextArea name="prep-reason" label="Why" value={reason} onChange={setReason} />
            <p className="muted">A drum pulled down on a vacuum pump is empty in a way a drum vented to
               atmosphere is not, so what is recorded afterwards follows what was actually done.</p>
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !ready}>
          {runner.busy ? 'Saving…'
            : open.what === 'fill' ? 'Record the fill'
            : open.what === 'check' ? 'Record the check'
            : open.what === 'inspect' ? 'Record what you found'
            : 'Record it as emptied'}
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ one cylinder's cycles */

/**
 * WHAT WAS IN THE DRUM WE SENT THEM IN MARCH — a question a customer asks after the event,
 * and which nothing in this system could answer before 0145.
 */
function CycleHistory({ cylinder, onClose }: { cylinder: FloorCylinder; onClose: () => void }) {
  const asked = useAsk(() => api.view('filling.cycles', { cylinderId: cylinder.cylinderId }), [cylinder.cylinderId], MESSAGES);
  type Row = Views['filling.cycles']['result'][number];
  const columns: readonly Column<Row>[] = [
    { key: 'cycle', head: 'Fill', cell: (r) => <>#{r.cycleNo}<div className="muted">{date(r.filledOn)}</div></> },
    { key: 'gas', head: 'Gas', cell: (r) => <>{r.gasService}<div className="muted">{r.quantity} {r.quantityUnit === 'm3' ? 'Nm³' : 'Kg'}</div></> },
    { key: 'batch', head: 'Batch', cell: (r) => <>{r.batchReference}<div className="muted">from {r.batchSourceRef}</div></> },
    { key: 'verdict', head: 'The laboratory', cell: (r) => r.qcOutcome === null
        ? <Pill tone="testing">not checked</Pill>
        : <><Pill tone={r.qcOutcome === 'passed' ? 'good' : 'danger'}>{r.qcOutcome}</Pill>
            <div className="muted">{date(r.qcOn)}{r.purityPct ? ` · ${r.purityPct}%` : ''}</div></> },
    { key: 'why', head: 'What was wrong', cell: (r) => r.qcReason ?? <span className="muted">—</span> },
    { key: 'analysis', head: 'Analysis', cell: (r) => r.analysisRef ?? <span className="muted">—</span> },
  ];
  return (
    <Card title={`Every filling of ${cylinder.internalId}`} actions={<Button small onClick={onClose}>Close</Button>}>
      {asked.loading ? <Loading what="the fill history" />
        : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
        : <Register columns={columns} rows={asked.data!}
                    empty="This cylinder has never been filled here. If it reads as full, it came in that way at registration or cutover." />}
    </Card>
  );
}

/* ------------------------------------------------------------------ the batch register */

const BATCH_COLUMNS: readonly Column<Batch>[] = [
  { key: 'ref', head: 'Batch', cell: (r) => <><strong>{r.reference}</strong><div className="muted">{r.gasService}</div></> },
  { key: 'from', head: 'Gas came from', cell: (r) => <>{r.sourceRef}<div className="muted">made {date(r.madeOn)}</div></> },
  { key: 'out', head: 'Cylinders filled', right: true, cell: (r) => (
      <>{count(r.plantFills)}{r.jobFills > 0 ? <div className="muted">+{count(r.jobFills)} on customer jobs</div> : null}</>) },
  { key: 'qty', head: 'Gas', right: true, cell: (r) => r.quantity
      ? <>{r.quantity} {r.quantityUnit === 'm3' ? 'Nm³' : 'Kg'}</> : <span className="muted">—</span> },
  // A BATCH IS JUDGED BY WHAT CAME OUT OF IT. Four failures out of a batch is a batch
  // somebody needs to look at, and that is not visible from a reference and a date.
  { key: 'verdicts', head: 'The laboratory', cell: (r) => (
      <>{r.passed > 0 ? <Pill tone="good">{`${r.passed} passed`}</Pill> : null}
        {r.failed > 0 ? <> <Pill tone="danger">{`${r.failed} failed`}</Pill></> : null}
        {r.unchecked > 0 ? <> <Pill tone="testing">{`${r.unchecked} not checked`}</Pill></> : null}
        {r.passed === 0 && r.failed === 0 && r.unchecked === 0 ? <span className="muted">—</span> : null}</>) },
  { key: 'analysis', head: 'Analysis', cell: (r) => r.analysisRef ?? <span className="muted">—</span> },
];
